import { decodeHoverIdleSaveData } from "@/features/hoverIdle/persistence/hoverIdleSaveCodec";
import { deserializeHuge, serializeHuge } from "@/shared/numbers";
import {
  appendAppliedRewardId,
  type OfflineRewardSaveMetadata,
} from "@/features/hoverIdle/persistence/offlineRewardSaveMetadata";
import {
  calculateTimReward,
  createOfflineRewardId,
  createOfflineRewardResultDigest,
} from "./createTimOfflineReward";
import { canonicalDigest, canonicalStringify } from "./offlineRewardDigest";
import { OFFLINE_REWARD_RECEIPT_VERSION } from "./offlineRewardReceipt";
import {
  OFFLINE_REWARD_FORMULA_VERSION,
  type OfflineRewardInput,
  type OfflineRewardResult,
  type OfflineRewardValidationResult,
} from "./offlineRewardTypes";

export function validateTimOfflineReward(
  input: OfflineRewardInput,
  result: OfflineRewardResult,
): OfflineRewardValidationResult {
  const errors: Extract<
    OfflineRewardValidationResult,
    { readonly ok: false }
  >["errors"][number][] = [];
  if (
    input.rewardFormulaVersion !== OFFLINE_REWARD_FORMULA_VERSION ||
    !Number.isFinite(input.gameElapsedMs) ||
    input.gameElapsedMs < 0 ||
    input.revision !== input.startSave.offlineReward.revision ||
    input.baseline.previousAt !== input.startSave.offlineReward.baselineAt ||
    input.baseline.committedAt < input.baseline.previousAt
  ) {
    errors.push({
      code: "invalid-input",
      path: "input",
      message: "offline reward input metadata is invalid",
    });
  }
  if (canonicalDigest(input.startSave) !== input.inputDigest) {
    errors.push({
      code: "digest-mismatch",
      path: "input.inputDigest",
      message: "offline reward input digest does not match the start save",
    });
  }

  const decoded = decodeHoverIdleSaveData(result.finalSave);
  if (!decoded.ok) {
    errors.push({
      code: "invalid-save",
      path: "result.finalSave",
      message: decoded.reason,
    });
  }

  const expectedTimDelta = serializeHuge(calculateTimReward(input));
  if (result.timDelta !== expectedTimDelta) {
    errors.push({
      code: "tim-reward",
      path: "result.timDelta",
      message: "Tim delta does not match the reward formula",
    });
  }
  const expectedRewardId = createOfflineRewardId(input);
  if (result.rewardId !== expectedRewardId) {
    errors.push({
      code: "receipt-mismatch",
      path: "result.rewardId",
      message: "reward ID does not match the reward input",
    });
  }

  const metadataWithoutReceipt: OfflineRewardSaveMetadata = {
    revision: input.revision + 1,
    baselineAt: input.baseline.committedAt,
    lastReceipt: null,
    appliedRewardIds: appendAppliedRewardId(
      input.startSave.offlineReward.appliedRewardIds,
      expectedRewardId,
    ),
  };
  const expectedFinalSaveWithoutReceipt = {
    ...input.startSave,
    economy: {
      ...input.startSave.economy,
      tim: serializeHuge(
        deserializeHuge(input.startSave.economy.tim).add(
          deserializeHuge(expectedTimDelta),
        ),
      ),
    },
    cumulativeStats: {
      ...input.startSave.cumulativeStats,
      totalTimEarned: serializeHuge(
        deserializeHuge(input.startSave.cumulativeStats.totalTimEarned).add(
          deserializeHuge(expectedTimDelta),
        ),
      ),
    },
    timestamps: {
      ...input.startSave.timestamps,
      savedAt: input.baseline.committedAt,
    },
    offlineReward: metadataWithoutReceipt,
  };
  const expectedResultDigest = createOfflineRewardResultDigest({
    finalSaveWithoutReceipt: expectedFinalSaveWithoutReceipt,
    rewardId: expectedRewardId,
    timDelta: expectedTimDelta,
  });
  const expectedReceipt = {
    version: OFFLINE_REWARD_RECEIPT_VERSION,
    rewardId: expectedRewardId,
    gameElapsedMs: input.gameElapsedMs,
    wasCapped: input.wasCapped,
    timGained: expectedTimDelta,
    resultDigest: expectedResultDigest,
    commitRevision: input.revision + 1,
    baseline: input.baseline,
  };
  if (
    result.resultDigest !== expectedResultDigest ||
    canonicalStringify(result.receipt) !== canonicalStringify(expectedReceipt)
  ) {
    errors.push({
      code: "receipt-mismatch",
      path: "result.receipt",
      message: "offline reward receipt or result digest is invalid",
    });
  }
  const expectedFinalSave = {
    ...expectedFinalSaveWithoutReceipt,
    offlineReward: {
      ...metadataWithoutReceipt,
      lastReceipt: expectedReceipt,
    },
  };
  if (
    canonicalStringify(result.finalSave) !==
    canonicalStringify(expectedFinalSave)
  ) {
    errors.push({
      code: "state-mismatch",
      path: "result.finalSave",
      message:
        "offline reward may change only Tim, totalTimEarned, timestamps, and reward metadata",
    });
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
